import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa'; // eslint-disable-line import/extensions

import './file-upload-accordian.scss';

const ACCORDIAN_STORAGE_KEY = 'fileUploadAccordianExpandedState';
const LEGACY_ACCORDIAN_STORAGE_PREFIX = 'fileUploadAccordianExpanded:';
const CUSTOM_STORAGE_GROUP = '__custom__';
const VOUCHER_ACCORDION_STORAGE_PREFIX = 'voucher:';

const getStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null;
  return window.sessionStorage;
};

const getLegacyStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  return window.localStorage;
};

const getCurrentPath = () => {
  if (typeof window === 'undefined' || !window.location) return 'unknown-path';
  return window.location.pathname || 'unknown-path';
};

const getAccordionState = () => {
  try {
    const persistedState = getStorage()?.getItem(ACCORDIAN_STORAGE_KEY);
    if (!persistedState) return {};
    return JSON.parse(persistedState) || {};
  } catch (e) {
    return {};
  }
};

const setAccordionState = (state) => {
  try {
    getStorage()?.setItem(ACCORDIAN_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // Ignore storage failures and keep the in-memory accordion state working.
  }
};

const getStateLocation = (storageKey, accordianTitle, currentPath = getCurrentPath()) => ({
  groupKey: storageKey ? CUSTOM_STORAGE_GROUP : currentPath,
  itemKey: storageKey || accordianTitle,
});

export const getVoucherAccordionStorageKey = (dealId, pageKey, sectionKey) => (
  `${VOUCHER_ACCORDION_STORAGE_PREFIX}${dealId}:${pageKey}:${sectionKey}`
);

export const clearVoucherAccordionState = (dealId) => {
  if (!dealId) return;
  try {
    const state = getAccordionState();
    const dealPrefix = `${VOUCHER_ACCORDION_STORAGE_PREFIX}${dealId}:`;
    const nextState = Object.keys(state).reduce((acc, groupKey) => {
      if (groupKey.includes(`/voucher/${dealId}/`)) return acc;
      if (groupKey !== CUSTOM_STORAGE_GROUP) {
        acc[groupKey] = state[groupKey];
        return acc;
      }

      const nextCustomState = Object.keys(state[groupKey] || {}).reduce((customAcc, itemKey) => {
        if (!itemKey.startsWith(dealPrefix)) customAcc[itemKey] = state[groupKey][itemKey];
        return customAcc;
      }, {});
      if (Object.keys(nextCustomState).length) acc[groupKey] = nextCustomState;
      return acc;
    }, {});
    setAccordionState(nextState);
  } catch (e) {
    // Ignore storage cleanup failures.
  }
};

const getPersistedExpanded = (storageKey, accordianTitle, currentPath) => {
  try {
    const { groupKey, itemKey } = getStateLocation(storageKey, accordianTitle, currentPath);
    return getAccordionState()?.[groupKey]?.[itemKey] === true;
  } catch (e) {
    return false;
  }
};

const setPersistedExpanded = (storageKey, accordianTitle, expanded, currentPath) => {
  try {
    const { groupKey, itemKey } = getStateLocation(storageKey, accordianTitle, currentPath);
    const state = getAccordionState();
    setAccordionState({
      ...state,
      [groupKey]: {
        ...(state[groupKey] || {}),
        [itemKey]: expanded,
      },
    });
  } catch (e) {
    // Ignore storage failures and keep the in-memory accordion state working.
  }
};

const cleanupLegacyAccordionStorage = () => {
  try {
    const storages = [getStorage(), getLegacyStorage()].filter(Boolean);
    const legacyKeys = [];
    storages.forEach((storage) => {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key && key.startsWith(LEGACY_ACCORDIAN_STORAGE_PREFIX)) legacyKeys.push({ storage, key });
      }
    });
    legacyKeys.forEach(({ storage, key }) => storage.removeItem(key));
  } catch (e) {
    // Ignore storage cleanup failures.
  }
};

const FileUploadAccordian = ({ children, accordianTitle, forceExpanded, onExpand, storageKey }) => {
  const currentPath = getCurrentPath();
  const [expanded, setExpanded] = useState(() => getPersistedExpanded(storageKey, accordianTitle, currentPath));
  const hasNotifiedExpandedRef = useRef(false);

  useEffect(() => {
    cleanupLegacyAccordionStorage();
  }, []);

  useEffect(() => {
    hasNotifiedExpandedRef.current = false;
    setExpanded(getPersistedExpanded(storageKey, accordianTitle, currentPath));
  }, [storageKey, accordianTitle, currentPath]);

  useEffect(() => {
    if (!forceExpanded) return;
    setPersistedExpanded(storageKey, accordianTitle, true, currentPath);
    setExpanded(true);
  }, [forceExpanded, storageKey, accordianTitle, currentPath]);

  useEffect(() => {
    if (!expanded) {
      hasNotifiedExpandedRef.current = false;
      return;
    }
    if (!hasNotifiedExpandedRef.current) {
      hasNotifiedExpandedRef.current = true;
      onExpand();
    }
  }, [expanded, onExpand]);

  const toggleExpanded = () => {
    setExpanded((previousExpanded) => {
      const nextExpanded = !previousExpanded;
      setPersistedExpanded(storageKey, accordianTitle, nextExpanded, currentPath);
      return nextExpanded;
    });
  };

  return (
    <>
      <div
        onClick={toggleExpanded}
        className={`accordian-header ${expanded ? 'expanded' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
      >
        <p className="accordian-title">{accordianTitle}</p>
        {expanded ? <FaChevronDown /> : <FaChevronUp />}
      </div>
      {expanded && <div className="accordian-body">{children}</div>}
      <div className="accordian-divider" />
    </>
  );
};

FileUploadAccordian.propTypes = {
  accordianTitle: PropTypes.string.isRequired,
  children: PropTypes.node,
  forceExpanded: PropTypes.bool,
  onExpand: PropTypes.func,
  storageKey: PropTypes.string,
};

FileUploadAccordian.defaultProps = {
  children: null,
  forceExpanded: false,
  onExpand: () => {},
  storageKey: '',
};

export default FileUploadAccordian;
